/** @jsxImportSource @emotion/react */
import { FileBadge } from "components/FileBadge/FileBadge";
import {
  CellValueRendererFileFieldsFragment,
  CellValueRendererFileFieldsFragmentDoc,
} from "graphql/_Types";
import {
  CellError,
  DetailedCellError,
  ErrorType,
} from "@inscopix/ideas-hyperformula";
import { useDataTableContext } from "../store/DataTableProvider";
import { gql, useFragment } from "@apollo/client";
import { CellValueRendererError } from "./CellValueRendererError";
import { memo, ReactNode, useState } from "react";
import { ModalArchiveDrsFile } from "components/ModalArchiveDrsFile/ModalArchiveDrsFile";
import { ModalUnarchiveDrsFiles } from "components/ModalUnarchiveDrsFile/ModalUnarchiveDrsFile";
import { ModalRenameDrsFile } from "components/ModalRenameDrsFile/ModalRenameDrsFile";
import { ModalIdentifyFile } from "components/ModalIdentifyFile/ModalIdentifyFile";
import { useFileUploadContext } from "stores/upload/FileUploadProvider";
import { FileStatus } from "types/constants";
import { client } from "providers/ApolloProvider/ApolloProvider";
import { useDraggable } from "@dnd-kit/core";
import { css } from "@emotion/react";
import { CellAddress } from "../store/DataTableProvider.types";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { ModalDeleteDrsFile } from "components/ModalDeleteDrsFile/ModalDeleteDrsFile";
import { generateDataTableDraggableId } from "../layout/DataTableDragDropProvider";

gql`
  fragment CellValueRendererFileFields on File {
    id
    name
    originalName
    status
    fileFormat
    fileType
    tags
    isSeries
    source
    processingStatus
    seriesParentId
    projectId
    referenceId
  }
`;

export const readFileCache = (id: string) => {
  return client.cache.readFragment<CellValueRendererFileFieldsFragment>({
    id: `File:${id}`,
    fragment: CellValueRendererFileFieldsFragmentDoc,
  });
};

export const writeToCache = (
  id: string,
  data: CellValueRendererFileFieldsFragment,
) => {
  return client.cache.writeFragment<CellValueRendererFileFieldsFragment>({
    id: `File:${id}`,
    fragment: CellValueRendererFileFieldsFragmentDoc,
    data,
  });
};

const useFileData = (fileId: string) => {
  return useFragment<CellValueRendererFileFieldsFragment>({
    fragment: CellValueRendererFileFieldsFragmentDoc,
    from: { __typename: "File", id: fileId },
  });
};

const styles = {
  draggable: css`
    height: auto;
    max-width: 100%;
    position: relative;
    width: auto;
    display: flex;
  `,
};

interface CellValueRendererFileProps {
  fileId: string;
  isCompact?: boolean;
  suppressContextMenu?: boolean;
  suppressTooltip?: boolean;
  isDraggable?: boolean;
  address?: CellAddress;
}

/** Component that renders an `IdeasFile` cell value` */
export const CellValueRendererFile = memo(function CellValueRendererFile(
  props: CellValueRendererFileProps,
) {
  const fileId = props.fileId;
  const setSelectedFileId = useDataTableContext((s) => s.setSelectedFileId);
  const deleteFile = useDataTableContext((s) => s.deleteFile);
  const unassignFile = useDataTableContext((s) => s.unassignFile);
  const isSelected = useDataTableContext((s) => s.selectedFileId === fileId);
  const hasEditPermission = useProjectPermissionStore(
    (s) => s.permissions["edit"],
  );
  const { data: file, complete } = useFileData(fileId);
  const [modal, setModal] = useState<ReactNode>(null);
  const isUploading = useFileUploadContext(
    (s) => s.filesById[fileId] !== undefined,
  );

  const [draggableId] = useState(() =>
    generateDataTableDraggableId({ address: props.address, fileId }),
  );

  const {
    attributes,
    listeners,
    setNodeRef: draggableRef,
  } = useDraggable({
    id: draggableId,
    disabled: !props.isDraggable || !hasEditPermission,
  });

  if (!complete) {
    const msg = "File not found";
    const error = new DetailedCellError(
      new CellError(ErrorType.ERROR, msg),
      msg,
    );
    return <CellValueRendererError error={error} />;
  }

  return (
    <>
      {modal}
      <div
        {...attributes}
        {...listeners}
        css={styles.draggable}
        ref={draggableRef}
      >
        <FileBadge
          drsFile={{
            id: file.id,
            name: file.name,
            // FIXME: Overwrite cell status for failed uploads. Ideally the
            // backend would do this cleanup when an upload is interrupted.
            status:
              (file.status === FileStatus.CREATED ||
                file.status === FileStatus.UPLOADING) &&
              !isUploading
                ? FileStatus.UPLOAD_CANCELLED
                : file.status,
            fileType: file.fileType,
            isSeries: file.isSeries,
            source: file.source,
            processingStatus: file.processingStatus,
            seriesParentId: file.seriesParentId,
          }}
          suppressContextMenu={props.suppressContextMenu}
          suppressTooltip={props.suppressTooltip}
          isSelected={isSelected}
          compact={props.isCompact}
          onClick={() => setSelectedFileId(file.id)}
          onDelete={() => {
            setModal(
              <ModalDeleteDrsFile
                drsFiles={[file]}
                onClose={() => setModal(null)}
              />,
            );
          }}
          onArchive={() => {
            setModal(
              <ModalArchiveDrsFile
                drsFiles={[file]}
                onClose={() => setModal(null)}
              />,
            );
          }}
          onUnarchive={() => {
            setModal(
              <ModalUnarchiveDrsFiles
                drsFiles={[file]}
                onClose={() => setModal(null)}
              />,
            );
          }}
          onRename={() => {
            setModal(
              <ModalRenameDrsFile
                drsFile={file}
                onClose={() => setModal(null)}
              />,
            );
          }}
          onIdentify={() => {
            setModal(
              <ModalIdentifyFile file={file} onClose={() => setModal(null)} />,
            );
          }}
          onCancelUpload={() =>
            void deleteFile({ fileId: file.id, mode: "delete" })
          }
          onUnassign={
            props.address !== undefined
              ? () =>
                  void unassignFile({
                    fileId: file.id,
                    address: props.address,
                  })
              : undefined
          }
        />
      </div>
    </>
  );
});
